import { Component } from 'preact';
import { TabBar } from './ui/TabBar';
import { t, k } from '../localization/t';
import RulesLanguageSwitch from './RulesLanguageSwitch';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { isWeb } from '../utils/platform';
import RuneSpinner from './RuneSpinner';
import { screenPanel } from '../utils/screenEnter';
import { getSoundSettings, saveSoundSettings, NOTIFICATION_SOUND_OPTIONS } from '../utils/arena/soundSettings';
import { getDiscordSettings, saveDiscordSettings } from '../utils/arena/discordSettings';
import { updateMusicVolume } from '../utils/arena/musicManager';
import { updateAmbienceVolume } from '../utils/arena/ambienceManager';
import { updateSubsurfaceAmbienceVolume } from '../utils/arena/subsurfaceAmbience';
import {
  updateCustomMusicVolume,
  getCustomMusicState,
  subscribeCustomMusic,
  hydrateCustomMusic,
} from '../utils/arena/customMusicManager';
import { UI } from '../utils/arena/uiSounds';
import { getLocalApiOrigin } from '../utils/localApi';
import { getGraphicsSettings, saveGraphicsSettings, RESOLUTION_PRESETS, QUALITY_PRESETS, applyPreset, detectPreset } from '../utils/game/graphicsSettings';
import {
  getAspectRatio,
  getAspectPreference,
  setAspectPreference,
  onAspectRatioChange,
  ASPECT_AUTO,
  ASPECT_16_9,
  ASPECT_16_10,
} from '../utils/uiScale';
import {
  getAccessibilitySettings, saveAccessibilitySettings,
  OWN_OUTLINE_SWATCHES, OPPONENT_OUTLINE_SWATCHES,
  OUTLINE_THICKNESS_MIN, OUTLINE_THICKNESS_MAX, OUTLINE_THICKNESS_STEP,
  COMBAT_SPEEDS,
} from '../utils/game/accessibilitySettings';
import { getControlSettings, saveControlSettings } from '../utils/game/controlSettings';
import { setTutorialsEnabled, resetAllTutorials } from '../utils/arena/tutorialState';
import { getUpdateChannel, setUpdateChannel, channelsUseSameServer, isChannelFromEnv } from '../utils/updateChannel';
import { switchChannel, CrossChannelSwitchError } from '../utils/crossChannelSwitch';
import { api } from '../utils/serverClient';
import { isDevBuild } from '../utils/devMode';
import { getChannelToken, setStoredToken } from '../utils/authApi';

// Client-side soft allowlist. Used only when the server's
// /auth/me/rc-eligible endpoint is unavailable (old server build that
// predates the allowlist work). Once the server is deployed, the
// server's answer is authoritative and this list is ignored. Names
// here are case-insensitive matched against the authenticated
// profile's display name.
const RC_SOFT_ALLOWLIST_NAMES = ['Clutterfox', 'ForceOfWill'];
function isInSoftAllowlist(profileName) {
  if (!profileName) return false;
  const n = String(profileName).toLowerCase();
  return RC_SOFT_ALLOWLIST_NAMES.some((entry) => entry.toLowerCase() === n);
}
import { Select } from './ui/select';
import { CloseButton } from './ui/navButtons';
import {
  IconScryingOrb,
  IconEye,
  IconKeyboard,
  IconBell,
  IconDiscord,
  IconCrown,
  IconScrollUpdate,
} from './ui/icons';
import {
  KEYBINDING_ACTIONS,
  getBinding as getKeybinding,
  setBinding as setKeybinding,
  resetBinding as resetKeybinding,
  resetAllBindings as resetAllKeybindings,
  describeBinding,
  bindingFromEvent,
  findActionByBinding,
} from '../utils/game/keybindings';
import {
  GOLD, GOLD_TEXT, BG_ATMOSPHERE, VIGNETTE,
  TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  BEVELED_BTN, GOLD_BTN,
  SettingsPill, SettingsSlider, SettingsToggle, SettingsSectionHeader,
  KnotOrnament,
} from '../lib/medievalTheme';

const SECTIONS = [
  { key: 'display', label: k('Display'), Icon: IconScryingOrb },
  { key: 'accessibility', label: k('Accessibility'), Icon: IconEye },
  { key: 'controls', label: k('Controls'), Icon: IconKeyboard },
  { key: 'sound', label: k('Sound'), Icon: IconBell },
  { key: 'discord', label: k('Discord'), Icon: IconDiscord },
  { key: 'account', label: k('Account'), Icon: IconCrown },
  { key: 'updates', label: k('Updates'), Icon: IconScrollUpdate },
  // Updater and Discord Rich Presence live in the Electron main
  // process; in a browser both sections would be dead panels.
].filter((s) => !(isWeb() && (s.key === 'updates' || s.key === 'discord')));

const ROW_BORDER = { borderBottom: `1px solid ${GOLD} 0.08)` };
// Panel container for a group of settings rows. Style comes from the
// .vh-settings-panel CSS class (inset bevel + warm inner shadow); the
// inline style only overrides for unusual call sites.
const PANEL_CLASS = 'vh-settings-panel flex flex-col overflow-hidden';

export default class SettingsScreen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: 'display',
      soundSettings: getSoundSettings(),
      discordSettings: getDiscordSettings(),
      graphicsSettings: getGraphicsSettings(),
      accessibilitySettings: getAccessibilitySettings(),
      controlSettings: getControlSettings(),
      displayMode: 'fullscreen',
      aspectRatio: getAspectRatio(),
      aspectPreference: getAspectPreference(),
      checking: false,
      retrying: false,
      tutorialReplayNote: null,
      discordHandleInput: props.profile?.discordHandle || '',
      discordHandleSaving: false,
      discordHandleError: null,
      channel: getUpdateChannel(),
      rcEligible: null,           // null = not yet fetched, true/false = answer (server-authoritative when available)
      rcEligibleFromServer: false, // did the server return an explicit answer? if not, soft allowlist applies
      rcEligibleChecking: false,
      channelSwitching: false,    // disables the picker during the cross-channel swap
      rebindingActionId: null,
      rebindConflict: null,
      keybindingTick: 0,
      customMusic: getCustomMusicState(),
    };
  }

  componentDidMount() {
    this.unsubCustomMusic = subscribeCustomMusic(() => {
      this.setState({ customMusic: getCustomMusicState() });
    });
    hydrateCustomMusic().catch(() => {});
    this.fetchRcEligibility();
  }

  fetchRcEligibility = async () => {
    this.setState({ rcEligibleChecking: true });
    try {
      const res = await api.get('/auth/me/rc-eligible');
      if (this._unmounted) return;
      this.setState({
        rcEligible: !!res?.eligible,
        rcEligibleFromServer: true,
        rcEligibleChecking: false,
      });
    } catch (err) {
      // Server doesn't have the endpoint yet (404), network failed,
      // etc. Fall back to the hardcoded soft allowlist. Enough to
      // gate the toggle for the trusted testers we know about until
      // the server-side enforcement ships.
      console.warn('[settings] RC eligibility check failed, falling back to soft allowlist:', err);
      if (this._unmounted) return;
      const eligible = isInSoftAllowlist(this.props.profile?.name);
      this.setState({
        rcEligible: eligible,
        rcEligibleFromServer: false,
        rcEligibleChecking: false,
      });
    }
  };


  startRebindCapture = (actionId) => {
    this.setState({ rebindingActionId: actionId, rebindConflict: null });
    window.addEventListener('keydown', this.handleRebindKeyDown, { capture: true });
  };

  stopRebindCapture = () => {
    window.removeEventListener('keydown', this.handleRebindKeyDown, { capture: true });
    if (this.state.rebindingActionId) {
      this.setState({ rebindingActionId: null });
    }
  };

  handleRebindKeyDown = (event) => {
    const actionId = this.state.rebindingActionId;
    if (!actionId) return;
    event.preventDefault();
    event.stopPropagation();
    // Escape cancels the capture without touching the current binding.
    if (event.key === 'Escape') {
      this.stopRebindCapture();
      return;
    }
    // Ignore bare modifier presses so the user can chord Shift+Key etc.
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
    const binding = bindingFromEvent(event);
    if (!binding) return;
    const conflict = findActionByBinding(binding, { exclude: actionId });
    if (conflict) setKeybinding(conflict, null);
    setKeybinding(actionId, binding);
    window.removeEventListener('keydown', this.handleRebindKeyDown, { capture: true });
    this.setState((s) => ({
      rebindingActionId: null,
      rebindConflict: conflict ? { actionId: conflict } : null,
      keybindingTick: s.keybindingTick + 1,
    }));
  };

  clearBinding = (actionId) => {
    setKeybinding(actionId, null);
    this.setState((s) => ({ keybindingTick: s.keybindingTick + 1 }));
  };

  resetBindingToDefault = (actionId) => {
    resetKeybinding(actionId);
    this.setState((s) => ({ keybindingTick: s.keybindingTick + 1, rebindConflict: null }));
  };

  resetAllKeybindings = () => {
    resetAllKeybindings();
    this.setState((s) => ({ keybindingTick: s.keybindingTick + 1, rebindConflict: null }));
  };

  saveDiscordHandle = () => {
    const { profile, onUpdateProfile } = this.props;
    if (!profile || !onUpdateProfile) return;
    const raw = this.state.discordHandleInput.trim().toLowerCase().replace(/^@+/, '');
    const next = raw === '' ? null : raw;
    // Mirror the server-side regex so we fail fast instead of
    // round-tripping a rejected update.
    if (next !== null && !/^[a-z0-9_.]{2,32}$/.test(next)) {
      this.setState({
        discordHandleError: t('Use 2-32 lowercase letters, digits, underscores, slashes, or periods.'),
      });
      return;
    }
    this.setState({ discordHandleSaving: true, discordHandleError: null });
    try {
      onUpdateProfile({ ...profile, discordHandle: next });
      this.setState({
        discordHandleSaving: false,
        discordHandleInput: next || '',
      });
    } catch (err) {
      this.setState({
        discordHandleSaving: false,
        discordHandleError: t('Could not save. Try again'),
      });
    }
  };

  resetAllTutorialsForAccount = () => {
    const profile = this.props.profile;
    if (!profile?.id) return;
    // Wipe every seen flag for this account and force the global
    // enable back on so the next auto-play checks all succeed. Used
    // primarily for QA testing. The in-game onboarding itself
    // relies on the individual flags, not this button.
    const cleared = resetAllTutorials(profile.id);
    setTutorialsEnabled(true);
    this.setState({
      tutorialsEnabled: true,
      tutorialReplayNote: cleared > 0
        ? (cleared === 1
          ? t("Reset 1 tutorial. They'll play again the next time you open each screen.")
          : t("Reset {n} tutorials. They'll play again the next time you open each screen.", { n: cleared }))
        : t('No tutorials were marked as seen yet. Everything is already fresh.'),
    });
  };

  setResolution = (resolution) => {
    if (!RESOLUTION_PRESETS[resolution]) return;
    saveGraphicsSettings({ resolution, preset: null });
    this.setState({ graphicsSettings: getGraphicsSettings() });
  };

  setAspectRatioPref = (pref) => {
    const safe = setAspectPreference(pref);
    this.setState({ aspectPreference: safe, aspectRatio: getAspectRatio() });
  };

  setBrightness = (value) => {
    const clamped = Math.round(Math.max(50, Math.min(150, value)));
    saveGraphicsSettings({ brightness: clamped / 100 });
    this.setState({ graphicsSettings: getGraphicsSettings() });
  };

  componentDidMount() {
    // Resolved aspect can change via two paths: the user picks a different
    // pill (manual), or. When on Auto. The window crosses to a different
    // monitor (display SSE feed). Re-read it either way to keep the canvas
    // letterboxing in sync.
    this.unsubAspect = onAspectRatioChange((a) => this.setState({ aspectRatio: a }));
    this.loadDisplayMode();
  }

  loadDisplayMode = async () => {
    try {
      const res = await fetch(`${getLocalApiOrigin()}/api/display/mode`);
      if (res.ok) {
        const { mode } = await res.json();
        this.setState({ displayMode: mode });
      }
    } catch {}
  };

  setDisplayMode = async (mode) => {
    this.setState({ displayMode: mode });
    try {
      await fetch(`${getLocalApiOrigin()}/api/display/mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
    } catch {}
  };

  componentWillUnmount() {
    this._unmounted = true;
    this.stopRebindCapture();
    this.unsubAspect?.();
    this.unsubCustomMusic?.();
  }

  updateSound = (key, value) => {
    const next = { ...this.state.soundSettings, [key]: value };
    saveSoundSettings(next);
    this.setState({ soundSettings: next });
  };

  toggleNotificationSound = (key) => {
    const cur = this.state.soundSettings.notificationSounds || {};
    this.updateSound('notificationSounds', { ...cur, [key]: cur[key] === false });
    updateMusicVolume();
    updateCustomMusicVolume();
    // Ambience owns a separate audio element and now scales with master too,
    // so it needs its own poke when the master slider moves.
    updateAmbienceVolume();
    updateSubsurfaceAmbienceVolume();
  };

  handleCheck = async () => {
    this.setState({ checking: true, lastCheckResult: null });
    await this.props.updateManager?.check();
    // After the check, if status is still UP_TO_DATE, show a confirmation
    const status = this.props.updateStatus || {};
    const result = status.state === 'UP_TO_DATE' ? 'no-update' : 'found';
    this.setState({ checking: false, lastCheckResult: result });
    // Clear the "no update" message after a few seconds
    if (result === 'no-update') {
      clearTimeout(this._checkResultTimer);
      this._checkResultTimer = setTimeout(() => {
        this.setState({ lastCheckResult: null });
      }, 5000);
    }
  };

  handleRetry = async () => {
    this.setState({ retrying: true });
    await this.props.updateManager?.retry();
    this.setState({ retrying: false });
  };

  updateDiscord = (key, value) => {
    const next = { ...this.state.discordSettings, [key]: value };
    saveDiscordSettings(next);
    this.setState({ discordSettings: next });
  };

  renderDiscordSection() {
    const ds = this.state.discordSettings;
    return (
      <div>
        <SettingsSectionHeader>{t('Discord')}</SettingsSectionHeader>
        <div className={PANEL_CLASS}>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <div>
              <span className="text-sm block" style={{ color: TEXT_PRIMARY }}>{t('Show Discord Activity')}</span>
              <span className="text-[0.625cqw] block mt-0.5" style={{ color: TEXT_MUTED }}>{t("Display what you're doing in Valkenhall on your Discord profile")}</span>
            </div>
            <SettingsToggle on={ds.showActivity} onClick={() => this.updateDiscord('showActivity', !ds.showActivity)} />
          </div>
          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <span className="text-sm block" style={{ color: TEXT_PRIMARY }}>{t('Allow Spectators')}</span>
              <span className="text-[0.625cqw] block mt-0.5" style={{ color: TEXT_MUTED }}>{t('Let Discord friends watch your matches via the Join button')}</span>
            </div>
            <SettingsToggle on={ds.allowSpectators} onClick={() => this.updateDiscord('allowSpectators', !ds.allowSpectators)} disabled={!ds.showActivity} />
          </div>
        </div>
      </div>
    );
  }

  setInputMode = (mode) => {
    saveControlSettings({ inputMode: mode });
    this.setState({ controlSettings: getControlSettings() });
  };

  setSnapToGrid = (on) => {
    saveControlSettings({ snapToGrid: on });
    this.setState({ controlSettings: getControlSettings() });
  };

  renderControlsSection() {
    const { inputMode, snapToGrid } = this.state.controlSettings;
    return (
      <div>
        <SettingsSectionHeader>{t('Controls')}</SettingsSectionHeader>
        <div className={PANEL_CLASS}>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <div>
              <div className="text-xs font-semibold" style={{ color: TEXT_PRIMARY }}>{t('Input Device')}</div>
              <div className="text-[0.625cqw] mt-0.5" style={{ color: TEXT_MUTED }}>
                {t('Controls how scroll/zoom works on the game board')}
              </div>
            </div>
            <div className="flex gap-1.5">
              <SettingsPill on={inputMode === 'mouse'} onClick={() => this.setInputMode('mouse')} className="px-3 py-1.5 text-[0.6875cqw] font-semibold">
                {t('Mouse')}
              </SettingsPill>
              <SettingsPill on={inputMode === 'trackpad'} onClick={() => this.setInputMode('trackpad')} className="px-3 py-1.5 text-[0.6875cqw] font-semibold">
                {t('Trackpad')}
              </SettingsPill>
            </div>
          </div>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <div>
              <div className="text-xs font-semibold" style={{ color: TEXT_PRIMARY }}>{t('Snap Cards to Squares')}</div>
              <div className="text-[0.625cqw] mt-0.5" style={{ color: TEXT_MUTED }}>
                {t('Dropped cards tuck into a tidy stack on their square. When off, cards rest exactly where you drop them')}
              </div>
            </div>
            <SettingsToggle on={snapToGrid} onClick={() => this.setSnapToGrid(!snapToGrid)} />
          </div>
          <div className="vh-panel-header px-5 py-4">
            <div className="text-[0.625cqw] leading-relaxed italic" style={{ color: 'rgba(166,160,155,0.55)' }}>
              {inputMode === 'mouse'
                ? t('Scroll wheel zooms in and out. Right-click and drag to pan around the board.')
                : t('Two-finger scroll pans around the board. Pinch to zoom in and out.')}
            </div>
          </div>
        </div>

        {this.renderKeybindingsList()}
      </div>
    );
  }

  renderKeybindingsList() {
    const { rebindingActionId, rebindConflict } = this.state;

    const sections = new Map();
    for (const action of KEYBINDING_ACTIONS) {
      if (!sections.has(action.section)) sections.set(action.section, []);
      sections.get(action.section).push(action);
    }

    const conflictAction = rebindConflict ? KEYBINDING_ACTIONS.find((a) => a.id === rebindConflict.actionId) : null;

    return (
      <div className="mt-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1"><SettingsSectionHeader>{t('Keybindings')}</SettingsSectionHeader></div>
          <SettingsPill on={false} onClick={this.resetAllKeybindings} className="shrink-0">
            {t('Reset All')}
          </SettingsPill>
        </div>

        {conflictAction ? (
          <div className="mb-2 px-3 py-2 text-[0.625cqw] rounded-md" style={{ background: `${GOLD} 0.08)`, color: TEXT_BODY, border: `1px solid ${GOLD} 0.18)` }}>
            {t('That key was bound to {action}. That action is now unbound.', { action: t(conflictAction.label) })}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          {Array.from(sections.entries()).map(([sectionLabel, actions]) => (
            <div key={sectionLabel} className={PANEL_CLASS}>
              <div className="vh-panel-header px-4 py-2 text-[0.5625cqw] font-semibold" style={{ color: `${GOLD} 0.65)`, textShadow: `0 0 10px ${GOLD} 0.2)`, borderBottom: `1px solid ${GOLD} 0.08)` }}>
                {t(sectionLabel)}
              </div>
              {actions.map((action, idx) => {
                const binding = getKeybinding(action.id);
                const isRebinding = rebindingActionId === action.id;
                const rowStyle = idx === actions.length - 1 ? null : ROW_BORDER;
                return (
                  <div key={action.id} className="flex items-center justify-between px-4 py-2" style={rowStyle}>
                    <span className="text-xs" style={{ color: TEXT_PRIMARY }}>{t(action.label)}</span>
                    <div className="flex items-center gap-2">
                      <SettingsPill
                        on={isRebinding}
                        onClick={() => (isRebinding ? this.stopRebindCapture() : this.startRebindCapture(action.id))}
                        className="px-2.5 py-1 !text-[0.6875cqw] !font-mono min-w-[4.375cqw] text-center"
                        style={isRebinding ? { borderStyle: 'dashed' } : { color: TEXT_PRIMARY }}
                        title={isRebinding ? t('Press any key (Esc to cancel)') : t('Click to rebind')}
                      >
                        {isRebinding ? t('Press key…') : describeBinding(binding)}
                      </SettingsPill>
                      <button
                        type="button"
                        onClick={() => this.resetBindingToDefault(action.id)}
                        className="text-[0.625cqw] cursor-pointer transition-colors"
                        style={{ color: TEXT_MUTED }}
                        title={t('Reset to default')}
                      >
                        {t('Reset')}
                      </button>
                      <button
                        type="button"
                        onClick={() => this.clearBinding(action.id)}
                        className="text-[0.625cqw] cursor-pointer transition-colors"
                        style={{ color: TEXT_MUTED }}
                        title={t('Clear binding')}
                      >
                        {t('Clear')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="mt-2 text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
          {t('Escape cancels rebind · Rebinding a key already in use will unbind the other action.')}
        </div>
      </div>
    );
  }

  renderSoundSection() {
    const ss = this.state.soundSettings;
    const cm = this.state.customMusic;
    const trackCount = cm.library?.tracks?.length || 0;
    const totalMB = (cm.library?.totalBytes || 0) / (1024 * 1024);
    const folderPath = cm.library?.folderPath || null;
    const customSummary = !folderPath
      ? t('No folder selected')
      : trackCount === 0
        ? t('Folder is empty')
        : trackCount === 1
          ? t('1 track · {mb} MB', { mb: totalMB < 1 ? '<1' : totalMB.toFixed(1) })
          : t('{n} tracks · {mb} MB', { n: trackCount, mb: totalMB < 1 ? '<1' : totalMB.toFixed(1) });
    return (
      <div>
        <SettingsSectionHeader>{t('Sound')}</SettingsSectionHeader>
        <div className={PANEL_CLASS}>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Master Volume')}</span>
            <div className="flex items-center gap-3">
              <SettingsSlider value={Math.round(ss.masterVolume * 100)} className="w-28" onInput={(e) => this.updateSound('masterVolume', parseInt(e.target.value, 10) / 100)} />
              <span className="text-xs w-8 text-right tabular-nums" style={{ color: TEXT_MUTED }}>{Math.round(ss.masterVolume * 100)}%</span>
            </div>
          </div>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Music')}</span>
            <div className="flex items-center gap-3">
              <SettingsToggle on={ss.musicEnabled} onClick={() => this.updateSound('musicEnabled', !ss.musicEnabled)} />
              <SettingsSlider value={Math.round(ss.musicVolume * 100)} disabled={!ss.musicEnabled} className="w-24" onInput={(e) => this.updateSound('musicVolume', parseInt(e.target.value, 10) / 100)} />
              <span className="text-xs w-8 text-right tabular-nums" style={{ color: TEXT_MUTED }}>{Math.round(ss.musicVolume * 100)}%</span>
            </div>
          </div>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Sound Effects')}</span>
            <div className="flex items-center gap-3">
              <SettingsToggle on={ss.sfxEnabled} onClick={() => this.updateSound('sfxEnabled', !ss.sfxEnabled)} />
              <SettingsSlider value={Math.round(ss.sfxVolume * 100)} disabled={!ss.sfxEnabled} className="w-24" onInput={(e) => this.updateSound('sfxVolume', parseInt(e.target.value, 10) / 100)} />
              <span className="text-xs w-8 text-right tabular-nums" style={{ color: TEXT_MUTED }}>{Math.round(ss.sfxVolume * 100)}%</span>
            </div>
          </div>
          <div className="px-5 pt-4 pb-1 text-xs" style={{ color: TEXT_MUTED }}>{t('Notification Sounds')}</div>
          {NOTIFICATION_SOUND_OPTIONS.map((o) => (
            <div key={o.key} className="flex items-center justify-between px-5 py-3" style={ROW_BORDER}>
              <div className="min-w-0 pr-4">
                <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t(o.label)}</span>
                <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>{t(o.hint)}</div>
              </div>
              <SettingsToggle on={ss.notificationSounds?.[o.key] !== false} disabled={!ss.sfxEnabled} onClick={() => this.toggleNotificationSound(o.key)} />
            </div>
          ))}
          {/* Custom music needs the native folder picker + fs watcher. */}
          {isWeb() ? null : (
            <div className="flex items-center justify-between px-5 py-4">
              <div className="min-w-0 pr-4">
                <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Custom Music Library')}</span>
                <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                  {t('Pick a folder on your device. The game watches it for audio files and plays them in-game. {summary}. Use the music button in the top bar to choose or change the folder.', { summary: customSummary })}
                </div>
              </div>
              {cm.isPlaying && cm.currentTrackId ? (
                <span
                  className="text-[0.625cqw] font-semibold px-2 py-1 rounded-md whitespace-nowrap"
                  style={{ background: `${GOLD} 0.15)`, border: `1px solid ${GOLD} 0.35)`, color: ACCENT_GOLD }}
                >
                  {t('Now playing')}
                </span>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  }

  renderAspectRatioRow() {
    const { aspectPreference } = this.state;

    return (
      <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
        <div className="min-w-0 pr-4">
          <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Aspect Ratio')}</span>
          <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
            {t('16:9 matches most modern monitors and TVs. 16:10 reclaims a bit of vertical space on taller monitors and laptops. Auto tracks whichever screen the window is currently on, so a laptop docked to an external monitor switches automatically.')}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <SettingsPill on={aspectPreference === ASPECT_AUTO} onClick={() => this.setAspectRatioPref(ASPECT_AUTO)}>
            {t('Auto')}
          </SettingsPill>
          <SettingsPill on={aspectPreference === ASPECT_16_9} onClick={() => this.setAspectRatioPref(ASPECT_16_9)}>
            16:9
          </SettingsPill>
          <SettingsPill on={aspectPreference === ASPECT_16_10} onClick={() => this.setAspectRatioPref(ASPECT_16_10)}>
            16:10
          </SettingsPill>
        </div>
      </div>
    );
  }

  renderDisplaySection() {
    const { displayMode, graphicsSettings } = this.state;
    const currentResolution = graphicsSettings.resolution;

    const activePreset = detectPreset(graphicsSettings);

    return (
      <div>
        <SettingsSectionHeader>{t('Display')}</SettingsSectionHeader>
        <div className={PANEL_CLASS}>
          {/* Graphics quality preset. One-click config */}
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <div>
              <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Graphics Quality')}</span>
              <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                {activePreset ? t(QUALITY_PRESETS[activePreset].desc) : t('Custom. Individual settings changed below')}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {Object.entries(QUALITY_PRESETS).map(([key, preset]) => (
                <SettingsPill
                  key={key}
                  on={activePreset === key}
                  onClick={() => {
                    applyPreset(key);
                    this.setState({ graphicsSettings: getGraphicsSettings() });
                  }}
                >
                  {t(preset.label)}
                </SettingsPill>
              ))}
            </div>
          </div>

          {/* Window mode toggles the Electron BrowserWindow; the browser
              owns its own window, so the row would be a dead control. */}
          {isWeb() ? null : (
            <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
              <div>
                <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Window Mode')}</span>
                <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>{t('How the game window is displayed')}</div>
              </div>
              <div className="flex items-center gap-2">
                <SettingsPill on={displayMode === 'fullscreen'} onClick={() => this.setDisplayMode('fullscreen')} className="px-3 py-1">
                  {t('Fullscreen')}
                </SettingsPill>
                <SettingsPill on={displayMode === 'windowed'} onClick={() => this.setDisplayMode('windowed')} className="px-3 py-1">
                  {t('Windowed')}
                </SettingsPill>
              </div>
            </div>
          )}
          {this.renderAspectRatioRow()}
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <div>
              <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Render Scale')}</span>
              <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>{t('Super-sampling for the 3D table. Lower for better FPS, higher for sharper visuals')}</div>
            </div>
            <div className="flex items-center gap-1.5">
              {Object.entries(RESOLUTION_PRESETS).map(([key, preset]) => (
                <SettingsPill
                  key={key}
                  on={currentResolution === key}
                  onClick={() => this.setResolution(key)}
                >
                  {t(preset.label)}
                </SettingsPill>
              ))}
            </div>
          </div>
          <div className="px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Brightness')}</span>
                <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>{t('Adjust overall screen brightness')}</div>
              </div>
              <div className="flex items-center gap-3">
                <SettingsSlider
                  min={50}
                  max={150}
                  value={Math.round((graphicsSettings.brightness ?? 1.0) * 100)}
                  className="w-28"
                  onInput={(e) => this.setBrightness(parseInt(e.target.value, 10))}
                />
                <span className="text-xs w-8 text-right tabular-nums" style={{ color: TEXT_MUTED }}>
                  {Math.round((graphicsSettings.brightness ?? 1.0) * 100)}%
                </span>
              </div>
            </div>
            {/* Brightness calibration strip. Three instances of the Norse
                rune at increasing luminance on a pure-black background. At
                correct brightness the left rune should be invisible, the
                center barely visible, and the right clearly visible. This is
                the standard "gamma calibration" pattern from AAA games. */}
            <div className="mt-3 flex items-stretch gap-px rounded-lg overflow-hidden" style={{ background: '#000', border: `1px solid ${GOLD} 0.1)` }}>
              {[
                { opacity: 0.025, label: t('Too dark') },
                { opacity: 0.07, label: t('Barely visible') },
                { opacity: 0.18, label: t('Visible') },
              ].map(({ opacity, label }) => (
                <div key={label} className="flex-1 flex flex-col items-center justify-center py-3" style={{ background: '#000' }}>
                  <img
                    src="/rune-divider.webp"
                    alt=""
                    draggable={false}
                    className="select-none"
                    style={{ width: '2.25cqw', height: '2.25cqw', opacity, filter: 'grayscale(1) brightness(2.5)' }}
                  />
                  <div className="text-[0.5cqw] mt-1.5 select-none" style={{ color: `rgba(255,255,255,${Math.min(opacity + 0.06, 0.25)})` }}>
                    {label}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1.5 text-[0.625cqw] text-center" style={{ color: TEXT_MUTED }}>
              {t('Adjust until the center rune is barely visible')}
            </div>
          </div>
        </div>

        {/* Post-processing effects */}
        <div className="mt-6"><SettingsSectionHeader>{t('Post-Processing')}</SettingsSectionHeader></div>
        <div className={PANEL_CLASS}>
          {[
            { key: 'ssao', label: t('Ambient Occlusion'), desc: t('Soft contact shadows between cards and surfaces') },
            { key: 'bloom', label: t('Bloom'), desc: t('Glow effect on foil cards and bright lights') },
            { key: 'vignette', label: t('Vignette'), desc: t('Darken the edges of the game board') },
            { key: 'antialiasing', label: t('Anti-Aliasing'), desc: t('Smooth jagged edges on cards and objects (SMAA)') },
            { key: 'candleLight', label: t('Candle Lighting'), desc: t('Candles and fireflies cast warm light on the board at night') },
          ].map((effect, i, arr) => (
            <div key={effect.key} className="flex items-center justify-between px-5 py-4" style={i < arr.length - 1 ? ROW_BORDER : {}}>
              <div>
                <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{effect.label}</span>
                <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>{effect.desc}</div>
              </div>
              <SettingsToggle
                on={graphicsSettings[effect.key] !== false}
                onClick={() => {
                  saveGraphicsSettings({ [effect.key]: graphicsSettings[effect.key] === false, preset: null });
                  this.setState({ graphicsSettings: getGraphicsSettings() });
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderAccessibilitySection() {
    const { accessibilitySettings: a } = this.state;
    const enabled = !!a.ownershipOutlines;

    const persist = (patch) => {
      saveAccessibilitySettings(patch);
      this.setState({ accessibilitySettings: getAccessibilitySettings() });
    };

    const renderSwatchRow = (label, desc, swatches, currentValue, patchKey) => (
      <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
        <div>
          <span className="text-sm" style={{ color: enabled ? TEXT_PRIMARY : TEXT_MUTED }}>{label}</span>
          <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>{desc}</div>
        </div>
        <div className="flex items-center gap-1.5" style={{ opacity: enabled ? 1 : 0.4 }}>
          {swatches.map((s) => {
            const isActive = currentValue?.toLowerCase() === s.value.toLowerCase();
            return (
              <button
                key={s.key}
                type="button"
                disabled={!enabled}
                className="w-6 h-6 rounded-full cursor-pointer transition-transform"
                style={{
                  background: s.value,
                  border: isActive ? `2px solid ${ACCENT_GOLD}` : `1px solid ${GOLD} 0.2)`,
                  transform: isActive ? 'scale(1.1)' : 'scale(1)',
                  boxShadow: isActive ? `0 0 8px ${s.value}` : '0 1px 2px rgba(0,0,0,0.4)',
                  cursor: enabled ? 'pointer' : 'not-allowed',
                }}
                data-sound={UI.CLICK}
                title={s.key}
                onClick={() => persist({ [patchKey]: s.value })}
              />
            );
          })}
        </div>
      </div>
    );

    return (
      <div>
        <SettingsSectionHeader>{t('Accessibility')}</SettingsSectionHeader>
        <div className={`${PANEL_CLASS} mb-5`}>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <div>
              <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Card Ownership Outlines')}</span>
              <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                {t("Paint a colored ring around every card in play so it's clear at a glance who controls which one. Off by default.")}
              </div>
            </div>
            <SettingsToggle on={enabled} onClick={() => persist({ ownershipOutlines: !enabled })} />
          </div>

          {renderSwatchRow(
            t('Your Cards'),
            t('Color of the outline around cards you control'),
            OWN_OUTLINE_SWATCHES,
            a.ownOutlineColor,
            'ownOutlineColor',
          )}
          {renderSwatchRow(
            t('Opponent Cards'),
            t('Color of the outline around cards your opponent controls'),
            OPPONENT_OUTLINE_SWATCHES,
            a.opponentOutlineColor,
            'opponentOutlineColor',
          )}

          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <span className="text-sm" style={{ color: enabled ? TEXT_PRIMARY : TEXT_MUTED }}>{t('Outline Thickness')}</span>
              <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                {t('How bold the ring is. Subtle at the low end, unmissable at the high end.')}
              </div>
            </div>
            <div className="flex items-center gap-3" style={{ opacity: enabled ? 1 : 0.4 }}>
              <SettingsSlider
                min={OUTLINE_THICKNESS_MIN}
                max={OUTLINE_THICKNESS_MAX}
                step={OUTLINE_THICKNESS_STEP}
                disabled={!enabled}
                value={a.outlineThickness}
                className="w-28"
                onInput={(e) => persist({ outlineThickness: parseFloat(e.target.value) })}
              />
              <span className="text-xs w-10 text-right tabular-nums" style={{ color: TEXT_MUTED }}>
                {a.outlineThickness.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <SettingsSectionHeader>{t('Motion')}</SettingsSectionHeader>
        <div className={PANEL_CLASS}>
          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Combat Animation Speed')}</span>
              <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                {t('How fast the blows land during combat. Clicking the stage still skips the rest.')}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {COMBAT_SPEEDS.map((v) => (
                <SettingsPill
                  key={v}
                  on={a.combatSpeed === v}
                  onClick={() => persist({ combatSpeed: v })}
                  className="px-3 py-1.5 text-[0.6875cqw] font-semibold"
                >
                  {v}&times;
                </SettingsPill>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderAccountSection() {
    const { profile, onChangeAvatar, onLogout, onQuit } = this.props;
    if (!profile) return null;
    const { tutorialReplayNote } = this.state;

    return (
      <div>
        {/* Profile info */}
        <SettingsSectionHeader>{t('Profile')}</SettingsSectionHeader>
        <div className={`${PANEL_CLASS} mb-5`}>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <div>
              <div className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Username')}</div>
              <div className="text-xs" style={{ color: TEXT_MUTED }}>{profile.name}</div>
            </div>
            <span className="text-[0.625cqw]" style={{ color: `${GOLD} 0.25)` }}>{t('Set at registration')}</span>
          </div>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <div>
              <div className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Email')}</div>
              <div className="text-xs" style={{ color: TEXT_MUTED }}>{profile.email || t('Not set')}</div>
            </div>
          </div>
          {onChangeAvatar ? (
            <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
              <div>
                <div className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Avatar')}</div>
                <div className="text-xs" style={{ color: TEXT_MUTED }}>{t('Change your profile picture')}</div>
              </div>
              <button
                type="button"
                className="px-3 py-1 text-xs font-medium cursor-pointer transition-all"
                style={{ ...BEVELED_BTN, color: `${GOLD_TEXT} 0.6)`, borderRadius: 'var(--radius-md)' }}
                onClick={() => { this.props.onBack(); onChangeAvatar(); }}
              >
                {t('Change')}
              </button>
            </div>
          ) : null}
          {this.props.onUpdateProfile ? (() => {
            const saved = profile.discordHandle || '';
            const dirty = this.state.discordHandleInput.trim().toLowerCase() !== saved;
            return (
              <div className="flex items-center justify-between px-5 py-4 gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Discord handle')}</div>
                  <div className="text-xs" style={{ color: this.state.discordHandleError ? '#d87a7a' : TEXT_MUTED }}>
                    {this.state.discordHandleError
                      || t('Shown on your profile so other players can find you on Discord')}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="text"
                    value={this.state.discordHandleInput}
                    onInput={(e) => this.setState({
                      discordHandleInput: e.target.value,
                      discordHandleError: null,
                    })}
                    onKeyDown={(e) => { if (e.key === 'Enter' && dirty) this.saveDiscordHandle(); }}
                    placeholder={t('yourhandle')}
                    maxLength={32}
                    className="px-2 py-1 text-xs"
                    style={{
                      background: 'rgba(0,0,0,0.3)',
                      border: `1px solid ${GOLD} 0.15)`,
                      borderRadius: 'var(--radius-md)',
                      color: TEXT_BODY,
                      width: '10cqw',
                    }}
                  />
                  <button
                    type="button"
                    disabled={!dirty || this.state.discordHandleSaving}
                    className="px-3 py-1 text-xs font-medium cursor-pointer transition-all"
                    style={{
                      ...BEVELED_BTN,
                      color: `${GOLD_TEXT} 0.6)`,
                      borderRadius: 'var(--radius-md)',
                      opacity: (!dirty || this.state.discordHandleSaving) ? 0.4 : 1,
                    }}
                    onClick={this.saveDiscordHandle}
                  >
                    {this.state.discordHandleSaving ? t('Saving…') : t('Save')}
                  </button>
                </div>
              </div>
            );
          })() : null}
        </div>

        {/* Tutorials + actions */}
        <SettingsSectionHeader>{t('Actions')}</SettingsSectionHeader>
        <div className={`${PANEL_CLASS} mb-5`}>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <div>
              <div className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Reset tutorials')}</div>
              <div className="text-xs" style={{ color: TEXT_MUTED }}>
                {tutorialReplayNote || t('Replay all onboarding walkthroughs from the beginning')}
              </div>
            </div>
            <button
              type="button"
              className="px-3 py-1 text-xs font-medium cursor-pointer transition-all"
              style={{ ...BEVELED_BTN, color: `${GOLD_TEXT} 0.6)`, borderRadius: 'var(--radius-md)' }}
              onClick={this.resetAllTutorialsForAccount}
            >
              {t('Reset')}
            </button>
          </div>
          {onLogout ? (
            <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
              <div>
                <div className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Log Out')}</div>
                <div className="text-xs" style={{ color: TEXT_MUTED }}>{t('Sign out and switch to another account')}</div>
              </div>
              <button type="button" className="px-3 py-1 text-xs cursor-pointer transition-all" style={{ ...BEVELED_BTN, borderRadius: 'var(--radius-md)', color: TEXT_BODY }}
                data-sound={UI.CANCEL}
                onClick={() => { this.props.onBack(); onLogout(); }}
              >
                {t('Log Out')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  handleChannelChange = async (nextChannel) => {
    const current = getUpdateChannel();
    if (nextChannel === current) return;
    if (this.state.channelSwitching) return;

    const sharedServer = channelsUseSameServer();
    const involvesLocal = nextChannel === 'local' || current === 'local';
    // Stable ↔ Beta on different servers now goes through the
    // cross-channel bridge: the user's session token is swapped
    // server-side without a manual magic-code round-trip, then the
    // renderer reloads onto the new server. Local Dev keeps the old
    // logout path because it doesn't share the HMAC secret with prod.
    const useBridge = !involvesLocal && !sharedServer;

    let warning;
    if (involvesLocal) {
      warning = nextChannel === 'local'
        ? t('Switching to Local Dev points the client at http://localhost:3000. You will be signed out and need to log in to your local server. Continue?')
        : t('Switching away from Local Dev will sign you out of the local server. Continue?');
    } else if (sharedServer) {
      warning = nextChannel === 'beta'
        ? t('Release Candidate builds may contain bugs or unfinished features. Continue?')
        : t('Switch back to the Stable desktop build?');
    } else {
      warning = nextChannel === 'beta'
        ? t('Switching to Release Candidates will reconnect to the staging server. The app will reload but you will stay signed in. Beta builds may contain bugs.\n\nContinue?')
        : t('Switching back to Stable will reconnect to the production server. The app will reload but you will stay signed in.\n\nContinue?');
    }
    // eslint-disable-next-line no-restricted-globals
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      if (!window.confirm(warning)) return;
    }

    // Tell the Electron main process about the channel flip. It
    // updates the auto-updater feed independently of the cross-channel
    // bridge, so this runs before either branch below.
    try {
      await fetch(`${getLocalApiOrigin()}/api/update/channel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel: nextChannel }),
      });
    } catch (err) {
      console.error('[settings] update channel POST failed:', err);
    }

    if (useBridge) {
      this.setState({ channelSwitching: true });

      // 1. Cheapest path: we already have a cached session token for
      //    the target channel (i.e. the user has previously logged in
      //    there). Swap to it, reload, done. No server round-trips
      //    needed. If the cached token is stale the reloaded app will
      //    fail its first request and bounce to the login screen,
      //    which is no worse than the legacy logout flow.
      const cached = getChannelToken(nextChannel);
      if (cached) {
        try {
          await setStoredToken(cached);
          setUpdateChannel(nextChannel);
          if (typeof window !== 'undefined' && window.location?.reload) {
            window.location.reload();
          }
          return;
        } catch (err) {
          console.warn('[settings] cached-token swap failed, falling through:', err);
        }
      }

      // 2. No cached token. Try the server-side cross-channel bridge.
      //    Only works once both servers have the new endpoints
      //    deployed; gracefully falls through to the logout flow below
      //    when the server 404s the /issue endpoint.
      try {
        await switchChannel(nextChannel);
        // switchChannel reloads on success, so the lines below won't
        // execute in practice. Kept defensive for the reload:false
        // case used in tests.
        this.setState({ channel: nextChannel, channelSwitching: false });
        return;
      } catch (err) {
        const stage = err instanceof CrossChannelSwitchError ? err.stage : 'unknown';
        const status = err instanceof CrossChannelSwitchError ? err.status : null;
        // Only stop the user when the current server explicitly said
        // "no." A 403 from /issue means they're not on the allowlist
        // and we should respect that. Every other failure (404 from
        // an undeployed server, network error, malformed response,
        // etc.) falls through to the legacy logout-and-relog path so
        // the switch still happens.
        if (stage === 'issue' && status === 403) {
          console.warn('[settings] cross-channel issue rejected:', err.message);
          toast.error(err.message);
          this.setState({ channelSwitching: false });
          return;
        }
        if (stage === 'validate') {
          toast.error(err.message);
          this.setState({ channelSwitching: false });
          return;
        }
        // Bridge unavailable (old server) or transient failure. Fall
        // back to the legacy force-logout-and-relog path. The user
        // re-enters a magic code; once they're in, the per-channel
        // cache populates so future switches are instant.
        console.warn('[settings] bridge unavailable, falling back to logout flow:', err.message);
        setUpdateChannel(nextChannel);
        this.setState({ channelSwitching: false });
        if (this.props.onLogout) this.props.onLogout();
        return;
      }
    }

    // Local-dev path keeps the old logout-and-relog flow.
    setUpdateChannel(nextChannel);
    this.setState({ channel: nextChannel });
    if (involvesLocal && this.props.onLogout) {
      this.props.onLogout();
    }
  };

  renderUpdateChannelPicker() {
    const current = this.state.channel || getUpdateChannel();
    const sharedServer = channelsUseSameServer();
    const fromEnv = isChannelFromEnv();
    const { rcEligible, rcEligibleChecking, channelSwitching } = this.state;
    // RC is gated by the server-side allowlist. Already being on beta
    // means the user has obviously been allowed there. Never grey out
    // the option that would let them go back to stable.
    const rcDisabled = current !== 'beta' && rcEligible === false;
    const rows = [
      { key: 'stable', label: t('Stable'), caption: t('Recommended for everyone. Receives tagged release builds.') },
      {
        key: 'beta',
        label: t('Release Candidates'),
        caption: sharedServer
          ? t('Receive pre-release desktop builds early. May be unstable.')
          : t('Pre-release builds against the staging server. May be unstable.'),
        disabled: rcDisabled,
        disabledReason: rcEligibleChecking
          ? t('Checking eligibility…')
          : t('Restricted to testers on the Release Candidates allowlist. Ask an admin to be added.'),
      },
    ];
    // Local Dev is only meaningful in unpackaged builds. Gating on
    // isDevBuild() (which the main process injects via ?dev=1) means
    // shipped users never see the option even if their pref somehow
    // ends up on 'local'.
    if (isDevBuild()) {
      rows.push({
        key: 'local',
        label: fromEnv && current === 'local' ? 'Local Dev (from env)' : 'Local Dev',
        caption: 'Connect to http://localhost:3000. For iterating on the server without deploying. Separate account.',
      });
    }
    return (
      <div className="mt-6">
        <SettingsSectionHeader>{t('Release Channel')}</SettingsSectionHeader>
        <div className={PANEL_CLASS}>
          {rows.map((row, i) => {
            const selected = current === row.key;
            const isDisabled = !!row.disabled || channelSwitching;
            const showLockReason = row.disabled && row.disabledReason;
            return (
              <button
                key={row.key}
                type="button"
                onClick={() => { if (!isDisabled) this.handleChannelChange(row.key); }}
                disabled={isDisabled}
                title={showLockReason ? row.disabledReason : undefined}
                className="flex items-start gap-3 px-5 py-4 text-left transition-colors"
                style={{
                  background: selected ? `${GOLD} 0.08)` : 'transparent',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled && !selected ? 0.45 : 1,
                  ...(i < rows.length - 1 ? ROW_BORDER : {}),
                }}
                onMouseEnter={(e) => { if (!selected && !isDisabled) e.currentTarget.style.background = `${GOLD} 0.04)`; }}
                onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
              >
                <span
                  className="shrink-0 mt-0.5 rounded-full"
                  style={{
                    width: '0.875cqw',
                    height: '0.875cqw',
                    border: `1.5px solid ${selected ? ACCENT_GOLD : 'rgba(200,180,120,0.35)'}`,
                    background: selected ? ACCENT_GOLD : 'transparent',
                    boxShadow: selected ? `0 0 6px ${GOLD} 0.4)` : 'none',
                  }}
                />
                <span className="flex flex-col min-w-0">
                  <span className="text-sm font-medium flex items-center gap-2" style={{ color: TEXT_PRIMARY }}>
                    {row.label}
                    {row.disabled ? (
                      <span
                        className="text-[0.5625cqw] font-semibold px-1.5 py-0.5 rounded"
                        style={{ color: TEXT_MUTED, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(166,160,155,0.2)' }}
                      >
                        {t('Locked')}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-[0.6875cqw] leading-snug mt-0.5" style={{ color: TEXT_MUTED }}>
                    {showLockReason ? row.disabledReason : row.caption}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  renderUpdatesSection() {
    const { updateStatus, onApply } = this.props;
    const { checking, retrying } = this.state;
    const status = updateStatus || {};

    const stateLabels = {
      UP_TO_DATE: t('Up to date'),
      CHECKING: t('Checking for updates...'),
      UPDATE_AVAILABLE: t('Update available'),
      DOWNLOADING: status.downloadProgress != null ? t('Downloading... {percent}%', { percent: status.downloadProgress }) : t('Downloading update...'),
      DOWNLOAD_FAILED: t('Download failed'),
      READY_TO_INSTALL: t('Update ready!'),
      APPLYING: t('Applying update...'),
    };

    const stateColors = {
      UP_TO_DATE: TEXT_MUTED,
      CHECKING: ACCENT_GOLD,
      UPDATE_AVAILABLE: ACCENT_GOLD,
      DOWNLOADING: ACCENT_GOLD,
      DOWNLOAD_FAILED: '#b04040',
      READY_TO_INSTALL: '#70b060',
      APPLYING: ACCENT_GOLD,
    };

    return (
      <div>
        <SettingsSectionHeader>{t('Updates')}</SettingsSectionHeader>
        <div className={PANEL_CLASS}>
          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Current Version')}</span>
            <span className="text-sm tabular-nums" style={{ color: TEXT_BODY }}>{status.currentVersion || t('Unknown')}</span>
          </div>

          <div className="flex items-center justify-between px-5 py-4" style={ROW_BORDER}>
            <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Status')}</span>
            <span className="text-sm font-medium" style={{ color: checking ? ACCENT_GOLD : this.state.lastCheckResult === 'no-update' ? '#70b060' : stateColors[status.state] || TEXT_MUTED }}>
              {checking
                ? t('Checking for updates...')
                : this.state.lastCheckResult === 'no-update'
                  ? t("You're on the latest version ✓")
                  : stateLabels[status.state] || status.state || t('Unknown')}
            </span>
          </div>

          {status.state === 'DOWNLOAD_FAILED' && status.error ? (
            <div className="px-4 py-2 text-xs" style={{ color: '#b04040', ...ROW_BORDER }}>
              {status.error}
            </div>
          ) : null}

          {status.state === 'DOWNLOADING' && status.downloadProgress != null ? (
            <div className="px-4 py-2" style={ROW_BORDER}>
              <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: `${GOLD} 0.1)` }}>
                <div className="h-full rounded-full transition-all duration-300" style={{ width: `${status.downloadProgress}%`, background: ACCENT_GOLD }} />
              </div>
            </div>
          ) : null}

          {status.releaseNotes && (status.state === 'READY_TO_INSTALL' || status.state === 'DOWNLOADING' || status.state === 'UPDATE_AVAILABLE') ? (
            <div className="px-5 py-4" style={ROW_BORDER}>
              <div className="text-[0.625cqw] font-semibold mb-2" style={{ color: `${GOLD} 0.4)` }}>
                {t("What's New in {version}", { version: status.newVersion || t('this update') })}
              </div>
              <div className="text-xs leading-relaxed max-h-32 overflow-y-auto" style={{ color: TEXT_BODY, whiteSpace: 'pre-wrap' }}>
                {status.releaseNotes}
              </div>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 px-5 py-4">
            {status.state === 'DOWNLOAD_FAILED' ? (
              <button type="button" className="px-4 py-1.5 text-xs font-medium cursor-pointer transition-all" style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }} disabled={retrying} onClick={this.handleRetry}>
                {retrying ? <span className="relative"><span className="invisible">{t('Retry Download')}</span><span className="absolute inset-0 flex items-center justify-center"><RuneSpinner size={16} /></span></span> : t('Retry Download')}
              </button>
            ) : null}
            {status.state === 'UP_TO_DATE' || status.state === 'DOWNLOAD_FAILED' ? (
              <button type="button" className="px-4 py-1.5 text-xs font-medium cursor-pointer transition-all" style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }} disabled={checking} onClick={this.handleCheck}>
                {checking ? <span className="relative"><span className="invisible">{t('Check for Updates')}</span><span className="absolute inset-0 flex items-center justify-center"><RuneSpinner size={16} /></span></span> : t('Check for Updates')}
              </button>
            ) : null}
            {status.state === 'READY_TO_INSTALL' ? (
              <button type="button" className="px-4 py-1.5 text-xs font-bold arena-heading cursor-pointer transition-all" style={{ ...GOLD_BTN, borderRadius: 'var(--radius-md)' }} data-sound={UI.CONFIRM} onClick={onApply}>
                {t('Restart to Update')}
              </button>
            ) : null}
          </div>
        </div>

        {this.renderUpdateChannelPicker()}
      </div>
    );
  }


  render() {
    const { onBack, profile } = this.props;
    const { activeSection } = this.state;

    // Filter sections based on available props
    const visibleSections = SECTIONS.filter((s) => {
      if (s.key === 'account' && !profile) return false;
      return true;
    });

    return (
      <div
        className="fixed inset-0 z-[100] flex flex-col"
        style={{
          background: `url("/tex-noise.webp"), ${BG_ATMOSPHERE}`,
          backgroundBlendMode: 'overlay, normal',
        }}
      >
        {/* Ambient vignette over the whole screen. Same treatment the hub
            and match view use so the settings feel like a sibling screen,
            not a floating dialog. */}
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        {/* Inner frame. Scales with viewport on very large displays so the
            whole layout grows with the window instead of anchoring at a
            fixed pixel size. */}
        <div className="relative flex-1 flex flex-col min-h-0">

          {/* Header: ceremonial title with flanking ornaments + close. */}
          <motion.div className="relative shrink-0 flex items-center justify-center pt-10 pb-5 px-12" {...screenPanel(1)}>
            <div className="flex items-center gap-8">
              <div
                className="h-px w-24"
                style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.5) 100%)` }}
              />
              <h1
                className="arena-heading font-bold"
                style={{
                  color: TEXT_PRIMARY,
                  fontSize: '2.125cqw',
                  textShadow: '0 3px 8px rgba(0,0,0,0.8), 0 0 22px rgba(212,168,67,0.25)',
                }}
              >
                {t('Settings')}
              </h1>
              <div
                className="h-px w-24"
                style={{ background: `linear-gradient(90deg, ${GOLD} 0.5) 0%, transparent 100%)` }}
              />
            </div>

            <div className="absolute right-12 top-10">
              <CloseButton title={t('Close')} onClick={onBack} />
            </div>
          </motion.div>

          {/* Horizontal tab bar. Flanking Borre knots anchor the rail at
              each end so the tabs don't float in empty space, and a
              full-width gold hairline under the row gives the active
              tab's indicator something to sit on. Like a menu rail
              carved into the wall of a AAA game's options screen. */}
          <motion.div className="shrink-0 px-12" {...screenPanel(2)}>
            <div className="relative flex items-end justify-center gap-4">
              <KnotOrnament side="left" style={{ marginBottom: 8 }} />
              <TabBar
                className="flex items-end justify-center flex-wrap"
                variant="chapter"
                barStyle={{ bottom: '-0.0625cqw' }}
                activeIndex={visibleSections.findIndex((s) => s.key === activeSection)}
              >
                {visibleSections.map((section) => {
                  const Icon = section.Icon;
                  return (
                    <button
                      key={section.key}
                      type="button"
                      data-tab-item
                      className={`vh-top-tab ${activeSection === section.key ? 'vh-top-tab-active' : ''}`}
                      onClick={() => this.setState({ activeSection: section.key })}
                    >
                      <Icon className="vh-top-tab-icon" size={18} />
                      {t(section.label)}
                    </button>
                  );
                })}
              </TabBar>
              <KnotOrnament side="right" style={{ marginBottom: 8 }} />
            </div>
            <div
              className="h-px mt-0"
              style={{
                background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.35) 12%, ${GOLD} 0.35) 88%, transparent 100%)`,
              }}
            />
          </motion.div>

          {/* Content area. Centered, generous side padding, a single
              readable column width (~1000px) so rows breathe. */}
          <motion.div className="flex-1 min-h-0 overflow-y-auto" {...screenPanel(3)}>
            <div className="mx-auto px-12 py-10 pb-16" style={{ maxWidth: '65cqw' }}>
              <SettingsSectionHeader>{t('General')}</SettingsSectionHeader>
              <div className={`${PANEL_CLASS} mb-8`}>
                <div className="flex items-center justify-between gap-4 px-5 py-4">
                  <div>
                    <span className="text-sm" style={{ color: TEXT_PRIMARY }}>{t('Language')}</span>
                    <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>{t('Menus, card text, keyword help and compendium')}</div>
                  </div>
                  <RulesLanguageSwitch />
                </div>
              </div>
              {activeSection === 'display' ? this.renderDisplaySection() : null}
              {activeSection === 'accessibility' ? this.renderAccessibilitySection() : null}
              {activeSection === 'controls' ? this.renderControlsSection() : null}
              {activeSection === 'sound' ? this.renderSoundSection() : null}
              {activeSection === 'discord' ? this.renderDiscordSection() : null}
              {activeSection === 'account' ? this.renderAccountSection() : null}
              {activeSection === 'updates' ? this.renderUpdatesSection() : null}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }
}
